import { FormLayoutGroup, Select, Stack, TextField } from '@customerio/pluma-components/react';
import { readInputValue } from '@/utils/dom';
import { isEmptyString } from '@/utils/string';
import type { CardEntryFields } from '../hooks/use-card-entry-fields';
import {
  COUNTRY_PLACEHOLDER,
  COUNTRY_SELECT_OPTIONS,
  isUnitedStates,
  isZipCodeIgnored,
  US_STATE_PLACEHOLDER,
  US_STATE_SELECT_OPTIONS,
} from '../utils/address-options';

export type BillingFieldsForm = Pick<
  CardEntryFields,
  'name' | 'address' | 'visibleErrors' | 'onNameInput' | 'onAddressChange' | 'onFieldBlur'
> & {
  canEdit: boolean;
};

type AddFundsBillingFieldsProps = {
  form: BillingFieldsForm;
};

export function AddFundsBillingFields({ form }: AddFundsBillingFieldsProps) {
  const { address, visibleErrors: errors } = form;
  const isDisabled = !form.canEdit;

  return (
    <Stack gap="150" data-test-add-funds-billing-fields>
      <TextField
        data-test-add-funds-name
        className="fs-mask"
        label="Name on card"
        value={form.name}
        error={errors.name}
        onInput={(event) => form.onNameInput(readInputValue(event))}
        onBlur={() => form.onFieldBlur('name')}
      />
      <TextField
        data-test-account-address-input="addressLine1"
        className="fs-mask"
        label="Address"
        isDisabled={isDisabled}
        value={address.addressLine1}
        error={errors.addressLine1}
        onInput={(event) => form.onAddressChange('addressLine1', readInputValue(event))}
        onBlur={() => form.onFieldBlur('addressLine1')}
      />
      <FormLayoutGroup gap="150">
        <TextField
          data-test-account-address-input="city"
          className="fs-mask"
          label="City"
          isDisabled={isDisabled}
          value={address.city}
          error={errors.city}
          onInput={(event) => form.onAddressChange('city', readInputValue(event))}
          onBlur={() => form.onFieldBlur('city')}
        />
        <TextField
          data-test-account-address-input="zipCode"
          className="fs-mask"
          label="Postal code"
          isDisabled={isDisabled || isZipCodeIgnored(address.country)}
          value={address.zipCode}
          error={errors.zipCode}
          onInput={(event) => form.onAddressChange('zipCode', readInputValue(event))}
          onBlur={() => form.onFieldBlur('zipCode')}
        />
      </FormLayoutGroup>
      <Select
        data-test-account-address-input="country"
        className="fs-mask"
        label="Country"
        placeholder={COUNTRY_PLACEHOLDER}
        options={COUNTRY_SELECT_OPTIONS}
        value={isEmptyString(address.country) ? null : address.country}
        isDisabled={isDisabled}
        isSearchable={true}
        withVirtualizer={true}
        error={errors.country}
        onChange={(value) => {
          form.onAddressChange('country', value ?? '');
          form.onFieldBlur('country');
        }}
      />
      {isUnitedStates(address.country) ? (
        <Select
          data-test-account-address-input="state"
          className="fs-mask"
          label="State"
          placeholder={US_STATE_PLACEHOLDER}
          options={US_STATE_SELECT_OPTIONS}
          value={isEmptyString(address.state) ? null : address.state}
          isDisabled={isDisabled}
          error={errors.state}
          onChange={(value) => {
            form.onAddressChange('state', value ?? '');
            form.onFieldBlur('state');
          }}
        />
      ) : null}
    </Stack>
  );
}
